// src/components/MapPicker/index.js
// ============================================
// MAP PICKER - Leaflet + OpenStreetMap (Free, No API Key)
// ============================================
// Features:
// - Interactive map with draggable marker
// - Click to place marker
// - Search locations with auto-suggest
// - Current location detection (GPS)
// - Reverse geocoding → auto-populates address fields
// - Confirm button returns structured address data
// ============================================

import React, { useEffect, useRef, useState, useCallback } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { X, MapPin, Navigation, Search, Loader } from 'lucide-react';
import './index.css';

// Fix Leaflet's default marker icon issue with webpack/CRA bundlers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const MapPicker = ({
    initialLocation,
    onConfirm,  // Called ONLY when user clicks "Confirm Location"
    onClose
}) => {
    const mapContainerRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const markerRef = useRef(null);

    // Internal state (NOT passed to parent until confirm)
    const [selectedCoords, setSelectedCoords] = useState(null);
    const [addressData, setAddressData] = useState(null);
    const [isReverseGeocoding, setIsReverseGeocoding] = useState(false);

    // Search state
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const searchTimerRef = useRef(null);

    // GPS state
    const [isLocating, setIsLocating] = useState(false);

    // Default center (Hyderabad)
    const defaultCenter = [17.3850, 78.4867];

    // ============================================
    // REVERSE GEOCODE (Nominatim - Free)
    // ============================================
    const reverseGeocode = useCallback(async (lat, lng) => {
        setIsReverseGeocoding(true);
        try {
            const res = await fetch(
                `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1&zoom=18`,
                {
                    headers: {
                        'Accept-Language': 'en',
                    }
                }
            );

            if (!res.ok) throw new Error('Geocode failed');

            const data = await res.json();
            const addr = data.address || {};

            // Build a detailed street address from all available parts
            const addressParts = [
                addr.house_number,
                addr.road || addr.pedestrian,
                addr.neighbourhood,
                addr.suburb,
                addr.city_district || addr.district
            ].filter(Boolean);

            // Use full display name parts as fallback if address is too short
            let streetAddress = addressParts.join(', ');
            if (streetAddress.length < 10 && data.display_name) {
                // Take enough parts from display_name to exceed 10 chars
                const displayParts = data.display_name.split(',').map(s => s.trim());
                streetAddress = displayParts.slice(0, 4).join(', ');
            }

            const structured = {
                display_name: data.display_name || '',
                address: streetAddress,
                apartment: addr.building || addr.house_number || '',
                city: addr.city || addr.town || addr.village || addr.state_district || addr.county || '',
                state: addr.state || '',
                zip_code: addr.postcode || '',
                country: addr.country || 'India',
                latitude: lat,
                longitude: lng
            };

            setAddressData(structured);
            return structured;
        } catch (e) {
            console.warn('Reverse geocode failed:', e);
            const fallback = {
                display_name: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
                address: '',
                apartment: '',
                city: '',
                state: '',
                zip_code: '',
                country: 'India',
                latitude: lat,
                longitude: lng
            };
            setAddressData(fallback);
            return fallback;
        } finally {
            setIsReverseGeocoding(false);
        }
    }, []);

    // ============================================
    // MOVE MARKER (internal only, does NOT call parent)
    // ============================================
    const moveMarkerTo = useCallback((lat, lng, shouldPan = true) => {
        const map = mapInstanceRef.current;
        const marker = markerRef.current;
        if (!map || !marker) return;

        const latlng = L.latLng(lat, lng);
        marker.setLatLng(latlng);
        if (shouldPan) {
            map.setView(latlng, Math.max(map.getZoom(), 16));
        }

        setSelectedCoords({ lat, lng });
        reverseGeocode(lat, lng);
    }, [reverseGeocode]);

    // ============================================
    // INITIALIZE LEAFLET MAP
    // ============================================
    useEffect(() => {
        if (!mapContainerRef.current || mapInstanceRef.current) return;

        const hasInitialLocation = initialLocation?.lat && initialLocation?.lng;
        const startLat = hasInitialLocation ? parseFloat(initialLocation.lat) : defaultCenter[0];
        const startLng = hasInitialLocation ? parseFloat(initialLocation.lng) : defaultCenter[1];

        // Create map
        const map = L.map(mapContainerRef.current, {
            center: [startLat, startLng],
            zoom: 16,
            zoomControl: true,
        });

        // Add OpenStreetMap tiles
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
            maxZoom: 19,
        }).addTo(map);

        // Create draggable marker
        const marker = L.marker([startLat, startLng], {
            draggable: true,
            autoPan: true
        }).addTo(map);

        // Marker drag end
        marker.on('dragend', () => {
            const pos = marker.getLatLng();
            setSelectedCoords({ lat: pos.lat, lng: pos.lng });
            reverseGeocode(pos.lat, pos.lng);
        });

        // Map click → move marker
        map.on('click', (e) => {
            const { lat, lng } = e.latlng;
            marker.setLatLng(e.latlng);
            setSelectedCoords({ lat, lng });
            reverseGeocode(lat, lng);
        });

        mapInstanceRef.current = map;
        markerRef.current = marker;

        // Set initial coordinates and reverse geocode them
        setSelectedCoords({ lat: startLat, lng: startLng });
        reverseGeocode(startLat, startLng);

        // Fix map rendering after DOM paint (safely)
        const rafId = requestAnimationFrame(() => {
            setTimeout(() => {
                try {
                    if (mapInstanceRef.current && mapContainerRef.current) {
                        mapInstanceRef.current.invalidateSize();
                    }
                } catch (e) {
                    console.warn('Map invalidateSize deferred:', e.message);
                }
            }, 100);
        });

        // Auto-detect current location on open (if no saved location)
        if (!hasInitialLocation && navigator.geolocation) {
            setIsLocating(true);
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const { latitude, longitude } = position.coords;
                    // Only move if map still exists (not unmounted)
                    if (mapInstanceRef.current && markerRef.current) {
                        const latlng = L.latLng(latitude, longitude);
                        markerRef.current.setLatLng(latlng);
                        mapInstanceRef.current.setView(latlng, 17);
                        setSelectedCoords({ lat: latitude, lng: longitude });
                        reverseGeocode(latitude, longitude);
                    }
                    setIsLocating(false);
                },
                (err) => {
                    console.warn('Auto-detect location failed:', err.message);
                    setIsLocating(false);
                },
                { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 }
            );
        }

        // Cleanup
        return () => {
            cancelAnimationFrame(rafId);
            map.remove();
            mapInstanceRef.current = null;
            markerRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ============================================
    // SEARCH LOCATIONS (Nominatim - Free)
    // ============================================
    const searchLocations = useCallback(async (query) => {
        if (!query || query.trim().length < 2) {
            setSearchResults([]);
            return;
        }

        setIsSearching(true);
        try {
            // Build viewbox from current map view for location-biased results
            let viewboxParam = '';
            if (mapInstanceRef.current) {
                const bounds = mapInstanceRef.current.getBounds();
                const sw = bounds.getSouthWest();
                const ne = bounds.getNorthEast();
                viewboxParam = `&viewbox=${sw.lng},${ne.lat},${ne.lng},${sw.lat}&bounded=0`;
            }

            const res = await fetch(
                `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=8&addressdetails=1&countrycodes=in${viewboxParam}`,
                {
                    headers: {
                        'Accept-Language': 'en',
                    }
                }
            );

            if (!res.ok) throw new Error('Search failed');

            const data = await res.json();
            setSearchResults(data || []);
        } catch (e) {
            console.error('Location search failed:', e);
            setSearchResults([]);
        } finally {
            setIsSearching(false);
        }
    }, []);

    // Debounced search (auto-search as user types)
    const handleSearchInputChange = (value) => {
        setSearchQuery(value);

        // Clear previous timer
        if (searchTimerRef.current) clearTimeout(searchTimerRef.current);

        if (value.trim().length >= 2) {
            searchTimerRef.current = setTimeout(() => {
                searchLocations(value);
            }, 400);
        } else {
            setSearchResults([]);
        }
    };

    // Select a search result
    const handleSelectResult = (result) => {
        const lat = parseFloat(result.lat);
        const lng = parseFloat(result.lon);

        moveMarkerTo(lat, lng, true);

        setSearchResults([]);
        setSearchQuery(result.display_name.split(',')[0]);
    };

    // ============================================
    // DETECT CURRENT LOCATION (GPS)
    // ============================================
    const handleDetectLocation = () => {
        if (!navigator.geolocation) {
            alert('Geolocation is not supported by your browser');
            return;
        }

        setIsLocating(true);
        navigator.geolocation.getCurrentPosition(
            (position) => {
                const { latitude, longitude } = position.coords;
                moveMarkerTo(latitude, longitude, true);
                setIsLocating(false);
            },
            (err) => {
                console.error('Location detection failed:', err);
                let msg = 'Unable to detect location.';
                if (err.code === 1) msg = 'Location access denied. Please enable location permissions.';
                if (err.code === 3) msg = 'Location request timed out. Please try again.';
                alert(msg);
                setIsLocating(false);
            },
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
        );
    };

    // ============================================
    // CONFIRM & CLOSE (sends data to parent)
    // ============================================
    const handleConfirm = () => {
        if (selectedCoords && addressData) {
            onConfirm({
                latitude: selectedCoords.lat,
                longitude: selectedCoords.lng,
                address: addressData.address,
                apartment: addressData.apartment,
                city: addressData.city,
                state: addressData.state,
                zip_code: addressData.zip_code,
                country: addressData.country,
                display_name: addressData.display_name
            });
        } else if (selectedCoords) {
            // Geocode failed but we still have coordinates
            onConfirm({
                latitude: selectedCoords.lat,
                longitude: selectedCoords.lng,
                address: '',
                apartment: '',
                city: '',
                state: '',
                zip_code: '',
                country: 'India',
                display_name: ''
            });
        }
    };

    // Cleanup search timer
    useEffect(() => {
        return () => {
            if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
        };
    }, []);

    // ============================================
    // RENDER
    // ============================================
    return (
        <div className="map-picker-backdrop" onClick={onClose}>
            <div className="map-picker-modal" onClick={(e) => e.stopPropagation()}>

                {/* Header */}
                <div className="map-picker-header">
                    <h3><MapPin size={18} /> Select Delivery Location</h3>
                    <button className="map-close-btn" onClick={onClose}><X size={20} /></button>
                </div>

                {/* Search Bar + GPS */}
                <div className="map-search-bar">
                    <div className="map-search-input-wrapper">
                        <Search size={16} className="map-search-icon" />
                        <input
                            type="text"
                            className="map-search-input"
                            placeholder="Search area, street, landmark..."
                            value={searchQuery}
                            onChange={(e) => handleSearchInputChange(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    searchLocations(searchQuery);
                                }
                            }}
                        />
                        {isSearching && <span className="map-searching">Searching...</span>}
                    </div>
                    <button
                        className="map-detect-btn"
                        onClick={handleDetectLocation}
                        disabled={isLocating}
                        title="Use my current location"
                    >
                        <Navigation size={16} />
                        <span>{isLocating ? 'Locating...' : 'My Location'}</span>
                    </button>
                </div>

                {/* Search Results Dropdown */}
                {searchResults.length > 0 && (
                    <div className="map-search-results">
                        {searchResults.map((result, i) => (
                            <button
                                key={i}
                                className="map-search-result-item"
                                onClick={() => handleSelectResult(result)}
                            >
                                <MapPin size={14} />
                                <div className="map-result-text">
                                    <span className="map-result-name">
                                        {result.display_name.split(',')[0]}
                                    </span>
                                    <span className="map-result-full">
                                        {result.display_name}
                                    </span>
                                </div>
                            </button>
                        ))}
                    </div>
                )}

                {/* Leaflet Map */}
                <div className="map-container" ref={mapContainerRef}></div>

                {/* Footer with address preview + confirm */}
                <div className="map-picker-footer">
                    {isReverseGeocoding ? (
                        <div className="map-selected-address loading">
                            <Loader size={14} className="map-spinner" />
                            <span>Fetching address...</span>
                        </div>
                    ) : addressData ? (
                        <div className="map-selected-address">
                            <MapPin size={14} />
                            <div className="map-address-details">
                                <span className="map-address-main">
                                    {addressData.address || addressData.display_name?.split(',').slice(0, 2).join(', ')}
                                </span>
                                {(addressData.city || addressData.state || addressData.zip_code) && (
                                    <span className="map-address-sub">
                                        {[addressData.city, addressData.state, addressData.zip_code].filter(Boolean).join(', ')}
                                    </span>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="map-selected-address placeholder">
                            <MapPin size={14} />
                            <span>Click on map or drag marker to select location</span>
                        </div>
                    )}

                    <button
                        className="confirm-location-btn"
                        onClick={handleConfirm}
                        disabled={!selectedCoords || isReverseGeocoding}
                    >
                        {isReverseGeocoding ? 'Loading...' : 'Confirm Location'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default MapPicker;
